import React, { useState, useEffect, useCallback, useContext, useRef, useMemo } from "react";
import api, { BASE_URL } from "../../Utils/api";
import { Button, Form, Spinner, Modal, Badge } from "react-bootstrap";
import * as XLSX from "xlsx";
import RGL from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import {
  FaEdit, FaSave, FaTimes, FaPlus, FaFilter, FaTrash,
  FaPencilAlt, FaCog, FaCheck, FaSync,
} from "react-icons/fa";
import { GlobalContext } from "../../Utils/GlobalContext";
import DashboardWidget from "./DashboardWidget";
import DashboardWidgetConfigModal, { CAMPOS_GRAFICO } from "./DashboardWidgetConfigModal";
import DocumentosModal from "../Documentos/DocumentosModal";

const { Responsive, WidthProvider } = RGL;
const ResponsiveGridLayout = WidthProvider(Responsive);

const LAYOUT_COLS = { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 };
const ROW_HEIGHT   = 120;

function gerarId() { return "w" + Math.random().toString(36).slice(2, 8); }

function formatarDDMMYYYY(d) {
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

// Converte Date objects e strings de data para dd/MM/yyyy com formato fixo (não depende de locale).
// Campos customizados são expandidos: cada entrada do JSON vira uma coluna plana na row.
export function normalizarLinha(row) {
  const out = {};
  for (const [k, v] of Object.entries(row)) {
    if (k === "campos customizados") {
      // Parse do objeto externo (mysql2 pode entregar string ou objeto já parseado)
      let cc;
      try {
        const raw = typeof v === "string" ? v : JSON.stringify(v ?? {});
        cc = JSON.parse(raw);
      } catch { cc = {}; }

      if (cc && typeof cc === "object" && !Array.isArray(cc)) {
        for (const [campo, metaRaw] of Object.entries(cc)) {
          let valor, tipo;

          if (metaRaw !== null && typeof metaRaw === "object" && !Array.isArray(metaRaw)) {
            // Estrutura encapsulada: { valor: ..., tipo_campo: ... }
            valor = "valor" in metaRaw ? (metaRaw["valor"] ?? null) : null;
            tipo  = String(metaRaw["tipo_campo"] ?? "").toUpperCase();
          } else {
            // Estrutura plana: o próprio metaRaw já é o valor (string, number, null)
            valor = metaRaw ?? null;
            // Detecta datas YYYY-MM-DD pelo padrão, já que tipo_campo não está disponível
            tipo  = (typeof valor === "string" && /^\d{4}-\d{2}-\d{2}$/.test(valor)) ? "DATA" : "";
          }

          if (valor == null) {
            out[campo] = null;
          } else if (tipo === "DATA") {
            const [y, m, d] = String(valor).split("-").map(Number);
            out[campo] = formatarDDMMYYYY(new Date(y, m - 1, d));
          } else {
            const s = String(valor);
            const num = parseFloat(s);
            out[campo] = (!isNaN(num) && Number.isInteger(num) && /^\d+(\.\d+)?$/.test(s))
              ? String(num)
              : s;
          }
        }
      }
      // "campos customizados" não entra no out — campos ficam expandidos individualmente
    } else if (v instanceof Date) {
      // Date object (mysql2 retorna assim para DATETIME/TIMESTAMP)
      out[k] = formatarDDMMYYYY(v);
    } else if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
      // "YYYY-MM-DD" sem horário (mysql2 retorna assim para DATE) — parse como data local
      const [y, m, d] = v.split("-").map(Number);
      out[k] = formatarDDMMYYYY(new Date(y, m - 1, d));
    } else if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
      // ISO com horário — parse normal (já em UTC, getDate/Month/FullYear usa local)
      const d = new Date(v);
      out[k] = isNaN(d) ? v : formatarDDMMYYYY(d);
    } else {
      out[k] = v;
    }
  }
  return out;
}

// ─── Helpers de data ─────────────────────────────────────────────────────────
// dd/MM/yyyy ↔ yyyy-MM-dd (input[type=date])
function toInputDate(s) {
  if (!s) return "";
  const p = String(s).split("/");
  return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : "";
}
function fromInputDate(s) {
  if (!s) return "";
  const p = String(s).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : "";
}

function parseDDMMYYYY(s) {
  if (!s) return null;
  const [d, m, y] = String(s).split("/");
  return d && m && y ? new Date(+y, +m - 1, +d) : null;
}

function valorPassaFiltroData(valor, de, ate) {
  if (!de && !ate) return true;   // sem filtro ativo → passa tudo
  if (!valor) return false;        // filtro ativo mas campo vazio → exclui
  const s = String(valor);
  // dd/MM/yyyy — comparação de data exata
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    const d = parseDDMMYYYY(s);
    if (!d) return true;
    if (de) { const dDe = parseDDMMYYYY(de); if (dDe && d < dDe) return false; }
    if (ate) { const dAte = parseDDMMYYYY(ate); if (dAte && d > dAte) return false; }
    return true;
  }
  // YYYY-MM — compara mês/ano
  if (/^\d{4}-\d{2}$/.test(s)) {
    if (de)  { const [, dm, dy] = de.split("/");  if (`${dy}-${dm}` > s)  return false; }
    if (ate) { const [, am, ay] = ate.split("/"); if (`${ay}-${am}` < s)  return false; }
    return true;
  }
  // YYYY — compara só o ano
  if (/^\d{4}$/.test(s)) {
    if (de)  { const dy = de.split("/")[2];  if (dy  > s) return false; }
    if (ate) { const ay = ate.split("/")[2]; if (ay  < s) return false; }
    return true;
  }
  return true;
}

// Label para exibição: usa CAMPOS_GRAFICO quando disponível, senão capitaliza o nome da coluna
function labelDoCampo(key) {
  const match = CAMPOS_GRAFICO.find((c) => c.value === key);
  if (match) return match.label;
  return key
    .split(/[\s_]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

// ─── Sidebar de filtros (valores gerados dos próprios dados) ──────────────────
function FiltroSidebar({ filtrosConfigurados, filtroValores, onChange, dadosRaw, filtrosData, filtrosValoresData, onFiltroDataChange, darkMode }) {
  const [modalFiltro, setModalFiltro] = useState(null);   // filtroKey aberto no modal
  const [selecaoTemp, setSelecaoTemp] = useState([]);     // seleção temporária no modal
  const [busca,       setBusca]       = useState("");     // busca no modal

  const bg    = darkMode ? "#161b27" : "#f8f9fa";
  const borda = darkMode ? "#30364a" : "#dee2e6";
  const texto = darkMode ? "#e2e8f0" : "#212529";

  if (!filtrosConfigurados.length && !filtrosData?.length) return null;

  const abrirModal = (key) => {
    setSelecaoTemp(filtroValores[key] ?? []);
    setBusca("");
    setModalFiltro(key);
  };

  const confirmarModal = () => {
    onChange({ ...filtroValores, [modalFiltro]: selecaoTemp });
    setModalFiltro(null);
  };

  const toggleTemp = (valor) =>
    setSelecaoTemp((prev) =>
      prev.includes(valor) ? prev.filter((v) => v !== valor) : [...prev, valor]
    );

  // Opções disponíveis no modal (todas as únicas do campo)
  const opcoesModal = modalFiltro
    ? [...new Set(dadosRaw.map((r) => r[modalFiltro]).filter((v) => v != null && v !== ""))].sort()
    : [];
  const opcoesFiltradas = busca
    ? opcoesModal.filter((o) => String(o).toLowerCase().includes(busca.toLowerCase()))
    : opcoesModal;

  return (
    <div style={{ width: 200, minWidth: 200, flexShrink: 0, display: "flex", flexDirection: "column", gap: 10 }}>
      {filtrosConfigurados.map((filtroKey) => {
        const selecionados = filtroValores[filtroKey] ?? [];
        return (
          <div key={filtroKey} style={{ background: bg, border: `1px solid ${borda}`, borderRadius: 8, overflow: "hidden" }}>
            {/* Cabeçalho */}
            <div style={{ display: "flex", alignItems: "center", padding: "8px 10px 6px", gap: 4 }}>
              <span style={{ flex: 1, fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#6c757d" }}>
                {labelDoCampo(filtroKey)}
              </span>
              <button
                onClick={() => abrirModal(filtroKey)}
                title="Selecionar valores"
                style={{ background: "none", border: "none", cursor: "pointer", padding: "1px 3px", color: "#6c757d", borderRadius: 3, display: "flex", alignItems: "center" }}
              >
                <FaPencilAlt size={10} />
              </button>
            </div>

            {/* Resumo dos selecionados */}
            <div style={{ padding: "0 10px 8px" }}>
              {selecionados.length === 0 ? (
                <span style={{ fontSize: "0.78rem", color: "#6c757d", fontStyle: "italic" }}>Todos</span>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {selecionados.slice(0, 4).map((v) => (
                    <div key={v} style={{ fontSize: "0.78rem", color: texto, display: "flex", alignItems: "center", gap: 4 }}>
                      <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#0d6efd", flexShrink: 0 }} />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v}</span>
                    </div>
                  ))}
                  {selecionados.length > 4 && (
                    <span style={{ fontSize: "0.72rem", color: "#6c757d" }}>+{selecionados.length - 4} mais</span>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Modal de seleção de valores */}
      <Modal show={!!modalFiltro} onHide={() => setModalFiltro(null)} centered scrollable size="sm">
        <Modal.Header closeButton style={{ background: darkMode ? "#161b27" : undefined, borderColor: borda }}>
          <Modal.Title style={{ color: texto, fontSize: "0.9rem" }}>
            {modalFiltro ? labelDoCampo(modalFiltro) : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: darkMode ? "#1c2235" : "#fff", padding: "10px 14px" }}>
          <Form.Control
            size="sm"
            placeholder="Buscar..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            style={{ marginBottom: 8, background: darkMode ? "#161b27" : undefined, color: texto, borderColor: borda }}
          />
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <span
              style={{ fontSize: "0.75rem", color: "#0d6efd", cursor: "pointer" }}
              onClick={() => setSelecaoTemp(opcoesModal)}
            >Todos</span>
            <span style={{ color: "#6c757d" }}>·</span>
            <span
              style={{ fontSize: "0.75rem", color: "#0d6efd", cursor: "pointer" }}
              onClick={() => setSelecaoTemp([])}
            >Nenhum</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {opcoesFiltradas.length === 0 ? (
              <span style={{ fontSize: "0.8rem", color: "#6c757d" }}>Nenhum resultado</span>
            ) : opcoesFiltradas.map((op) => (
              <Form.Check
                key={op}
                type="checkbox"
                label={op}
                checked={selecaoTemp.includes(op)}
                onChange={() => toggleTemp(op)}
                style={{ fontSize: "0.82rem", color: texto, marginBottom: 1 }}
              />
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer style={{ background: darkMode ? "#161b27" : undefined, borderColor: borda }} className="modal-footer-padrao">
          <div className="modal-footer-acoes-esquerda" />
          <div className="modal-footer-acoes-direita">
            <Button variant="light" className="modal-footer-btn-cancelar" onClick={() => setModalFiltro(null)}>Cancelar</Button>
            <Button variant="primary" className="modal-footer-btn-salvar" onClick={confirmarModal}>Aplicar</Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Filtros de intervalo de data (auto-adicionados por widgets Linha do Tempo) */}
      {(filtrosData ?? []).map(({ campo, rotulo }) => {
        const range = filtrosValoresData?.[campo] ?? {};
        return (
          <div key={campo} style={{ background: bg, border: `1px solid ${borda}`, borderRadius: 8, overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", padding: "8px 10px 6px", gap: 4 }}>
              <span style={{ flex: 1, fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#6c757d" }}>
                {rotulo}
              </span>
              {(range.de || range.ate) && (
                <button
                  onClick={() => onFiltroDataChange(campo, "de", "") || onFiltroDataChange(campo, "ate", "")}
                  title="Limpar intervalo"
                  style={{ background: "none", border: "none", cursor: "pointer", padding: "1px 3px", color: "#dc3545", display: "flex", alignItems: "center" }}
                >
                  <FaTimes size={9} />
                </button>
              )}
            </div>
            <div style={{ padding: "0 10px 10px", display: "flex", flexDirection: "column", gap: 5 }}>
              {[{ key: "de", label: "De" }, { key: "ate", label: "Até" }].map(({ key, label }) => (
                <div key={key} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: "0.7rem", color: "#6c757d", width: 22, flexShrink: 0 }}>{label}</span>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={toInputDate(range[key])}
                    onChange={(e) => onFiltroDataChange(campo, key, fromInputDate(e.target.value))}
                    style={{ background: darkMode ? "#161b27" : undefined, color: texto, borderColor: borda, fontSize: "0.78rem", colorScheme: darkMode ? "dark" : "light" }}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <Button
        variant="outline-secondary"
        size="sm"
        onClick={() => onChange({})}
        style={{ fontSize: "0.78rem" }}
      >
        <FaTimes className="me-1" size={10} /> Limpar Filtros
      </Button>
    </div>
  );
}

// ─── Modal configurar filtros ─────────────────────────────────────────────────
function FiltrosConfigModal({ show, onClose, filtrosConfigurados, filtrosValoresPadrao, dadosRaw, camposDisponiveis, camposData, filtrosData, filtrosValoresData, onSalvar, darkMode }) {
  const [selecionados,      setSelecionados]      = useState([]);
  const [valoresPadrao,     setValoresPadrao]     = useState({});
  const [selecionadosData,  setSelecionadosData]  = useState([]); // keys de campos data ativos
  const [valoresPadraoData, setValoresPadraoData] = useState({}); // {campo: {de, ate}}
  const [busca,             setBusca]             = useState("");

  const camposDataSet = useMemo(() => new Set((camposData ?? []).map((c) => c.value)), [camposData]);

  useEffect(() => {
    if (!show) return;
    setSelecionados(filtrosConfigurados ?? []);
    setValoresPadrao(filtrosValoresPadrao ?? {});
    setSelecionadosData((filtrosData ?? []).map((f) => f.campo));
    setValoresPadraoData(filtrosValoresData ?? {});
    setBusca("");
  }, [show, filtrosConfigurados, filtrosValoresPadrao, filtrosData, filtrosValoresData]);

  const toggleFiltro = (key) => {
    if (camposDataSet.has(key)) {
      setSelecionadosData((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
    } else {
      setSelecionados((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
    }
  };

  const toggleValorPadrao = (key, valor) =>
    setValoresPadrao((prev) => {
      const atual = prev[key] ?? [];
      return { ...prev, [key]: atual.includes(valor) ? atual.filter((v) => v !== valor) : [...atual, valor] };
    });

  const handleDataChange = (key, tipo, valor) =>
    setValoresPadraoData((prev) => ({
      ...prev,
      [key]: { ...(prev[key] ?? {}), [tipo]: valor },
    }));

  const bg    = darkMode ? "#1c2235" : "#fff";
  const borda = darkMode ? "#30364a" : "#dee2e6";
  const texto = darkMode ? "#e2e8f0" : "#212529";
  const bgSec = darkMode ? "#161b27" : "#f8f9fa";

  const camposFiltrados = camposDisponiveis.filter((key) =>
    busca === "" || labelDoCampo(key).toLowerCase().includes(busca.toLowerCase()) || key.toLowerCase().includes(busca.toLowerCase())
  );

  const handleSalvar = () => {
    const novosFiltrosData = selecionadosData.map((campo) => ({ campo, rotulo: labelDoCampo(campo) }));
    onSalvar(selecionados, valoresPadrao, novosFiltrosData, valoresPadraoData);
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg" scrollable>
      <Modal.Header closeButton style={{ background: darkMode ? "#161b27" : undefined, borderColor: borda }}>
        <Modal.Title style={{ color: texto, fontSize: "0.95rem" }}>
          Configurar Filtros
          <div style={{ fontSize: "0.75rem", fontWeight: 400, color: "#6c757d", marginTop: 2 }}>
            Selecione os filtros e defina valores padrão para este dashboard
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ background: bg }}>
        <Form.Control
          size="sm"
          placeholder="Buscar campo..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          style={{ marginBottom: 12, background: darkMode ? "#161b27" : undefined, color: texto, borderColor: borda }}
        />

        {camposFiltrados.length === 0 && (
          <div style={{ fontSize: "0.83rem", color: "#6c757d", textAlign: "center", padding: "24px 0" }}>
            Nenhum campo encontrado
          </div>
        )}

        {camposFiltrados.map((key) => {
          const isData = camposDataSet.has(key);
          const ativo  = isData ? selecionadosData.includes(key) : selecionados.includes(key);
          const opcoes = isData ? [] : [...new Set(
            dadosRaw.map((r) => r[key]).filter((v) => v != null && v !== "")
          )].sort();
          const rangeAtual = valoresPadraoData[key] ?? {};

          return (
            <div
              key={key}
              style={{
                marginBottom: 8,
                borderRadius: 8,
                border: `1px solid ${ativo ? "#0d6efd55" : borda}`,
                background: ativo ? (darkMode ? "#1a2d50" : "#f0f5ff") : bgSec,
                overflow: "hidden",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", padding: "9px 14px", gap: 10 }}>
                <Form.Check
                  type="checkbox"
                  checked={ativo}
                  onChange={() => toggleFiltro(key)}
                  style={{ marginBottom: 0 }}
                />
                <div>
                  <span style={{ fontWeight: 600, fontSize: "0.85rem", color: texto }}>{labelDoCampo(key)}</span>
                  <span style={{ fontSize: "0.7rem", color: "#6c757d", marginLeft: 8, fontFamily: "monospace" }}>{key}</span>
                </div>
                <span style={{ fontSize: "0.72rem", color: "#6c757d", marginLeft: "auto" }}>
                  {isData ? "intervalo de datas" : opcoes.length > 0 ? `${opcoes.length} valor${opcoes.length !== 1 ? "es" : ""}` : ""}
                </span>
              </div>

              {ativo && isData && (
                <div style={{ padding: "0 14px 12px 38px", borderTop: `1px solid ${borda}` }}>
                  <div style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#6c757d", margin: "8px 0 6px" }}>
                    Intervalo padrão (pré-preenchido ao abrir)
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {[{ key: "de", label: "De" }, { key: "ate", label: "Até" }].map(({ key: k, label }) => (
                      <div key={k} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: "0.75rem", color: "#6c757d", width: 26, flexShrink: 0 }}>{label}</span>
                        <Form.Control
                          type="date"
                          size="sm"
                          value={toInputDate(rangeAtual[k])}
                          onChange={(e) => handleDataChange(key, k, fromInputDate(e.target.value))}
                          style={{ background: darkMode ? "#161b27" : undefined, color: texto, borderColor: borda, fontSize: "0.82rem", colorScheme: darkMode ? "dark" : "light", maxWidth: 180 }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {ativo && !isData && (
                <div style={{ padding: "0 14px 12px 38px", borderTop: `1px solid ${borda}` }}>
                  <div style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#6c757d", margin: "8px 0 6px" }}>
                    Valor padrão (pré-selecionado ao abrir)
                  </div>
                  <div style={{ maxHeight: 140, overflowY: "auto" }}>
                    {opcoes.length === 0 ? (
                      <span style={{ fontSize: "0.78rem", color: "#6c757d" }}>Sem dados disponíveis ainda</span>
                    ) : (
                      opcoes.map((op) => (
                        <Form.Check
                          key={op}
                          type="checkbox"
                          label={op}
                          checked={(valoresPadrao[key] ?? []).includes(op)}
                          onChange={() => toggleValorPadrao(key, op)}
                          style={{ fontSize: "0.82rem", color: texto, marginBottom: 2 }}
                        />
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </Modal.Body>

      <Modal.Footer style={{ background: darkMode ? "#161b27" : undefined, borderColor: borda }} className="modal-footer-padrao">
        <div className="modal-footer-acoes-esquerda" />
        <div className="modal-footer-acoes-direita">
          <Button variant="light" className="modal-footer-btn-cancelar" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" className="modal-footer-btn-salvar" onClick={handleSalvar}>
            Aplicar
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

// ─── Modal dados brutos ───────────────────────────────────────────────────────
function DadosBrutosModal({ show, onClose, onRecarregar, dadosRaw, dadosCarregando, entidadeID, darkMode }) {
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const exportarExcel = () => {
    if (!dadosRaw.length) return;
    const ws = XLSX.utils.json_to_sheet(dadosRaw);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Dados");
    XLSX.writeFile(wb, `dados_brutos_${entidadeID}.xlsx`);
  };

  const handleSort = (col) => {
    if (sortCol === col) {
      if (sortDir === "asc") setSortDir("desc");
      else { setSortCol(null); setSortDir("asc"); }
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  const dadosOrdenados = useMemo(() => {
    if (!sortCol || !dadosRaw.length) return dadosRaw;
    return [...dadosRaw].sort((a, b) => {
      const va = a[sortCol] ?? "";
      const vb = b[sortCol] ?? "";
      const cmp = String(va).localeCompare(String(vb), "pt", { numeric: true, sensitivity: "base" });
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [dadosRaw, sortCol, sortDir]);

  const borda = darkMode ? "#30364a" : "#dee2e6";
  const thStyle = (col) => ({
    padding: "6px 10px",
    border: `1px solid ${borda}`,
    fontWeight: 700,
    fontFamily: "monospace",
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
    background: sortCol === col
      ? (darkMode ? "#1e3464" : "#dbeafe")
      : (darkMode ? "#1c2235" : "#e9ecef"),
    color: sortCol === col ? "#0d6efd" : (darkMode ? "#e2e8f0" : "#212529"),
  });

  return (
    <Modal show={show} onHide={onClose} centered size="xl" scrollable>
      <Modal.Header closeButton style={{ background: darkMode ? "#161b27" : undefined, borderColor: borda }}>
        <Modal.Title style={{ color: darkMode ? "#e2e8f0" : "#212529", fontSize: "0.92rem" }}>
          Dados brutos
          {sortCol && (
            <span style={{ fontSize: "0.72rem", fontWeight: 400, color: "#6c757d", marginLeft: 10 }}>
              ordenado por <strong style={{ fontFamily: "monospace" }}>{sortCol}</strong> {sortDir === "asc" ? "↑" : "↓"}
              <span
                style={{ marginLeft: 6, cursor: "pointer", color: "#dc3545" }}
                onClick={() => { setSortCol(null); setSortDir("asc"); }}
                title="Remover ordenação"
              >✕</span>
            </span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ background: darkMode ? "#0f1623" : "#f8f9fa", padding: 0 }}>
        {dadosCarregando ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
            <Spinner animation="border" size="sm" className="me-2" />
            <span style={{ fontSize: "0.85rem", color: "#6c757d" }}>Buscando dados...</span>
          </div>
        ) : dadosRaw.length === 0 ? (
          <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: 200, gap: 10 }}>
            <span role="img" aria-label="Lupa" style={{ fontSize: "2rem", opacity: 0.3 }}>🔍</span>
            <div style={{ fontSize: "0.88rem", color: "#6c757d", fontWeight: 500 }}>Nenhuma linha retornada</div>
            <div style={{ fontSize: "0.78rem", color: "#6c757d", maxWidth: 420, textAlign: "center" }}>
              Verifique se a procedure <code>sp_relatorio_documentos</code> foi executada no banco,
              se há documentos cadastrados para a entidade <strong>{entidadeID}</strong> e se os JOINs
              da procedure retornam registros.
            </div>
            <Button size="sm" variant="outline-secondary" onClick={() => { onClose(); onRecarregar(); }}>
              <FaSync size={11} className="me-1" /> Tentar novamente
            </Button>
          </div>
        ) : (() => {
          const colunas = Object.keys(dadosRaw[0]);
          return (
            <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "65vh" }}>
              <table style={{
                borderCollapse: "collapse", fontSize: "0.75rem", whiteSpace: "nowrap",
                color: darkMode ? "#e2e8f0" : "#212529", width: "100%",
              }}>
                <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
                  <tr>
                    <th style={{ padding: "6px 10px", border: `1px solid ${borda}`, color: "#6c757d", fontWeight: 700, textAlign: "center", background: darkMode ? "#1c2235" : "#e9ecef" }}>#</th>
                    {colunas.map((col) => (
                      <th key={col} style={thStyle(col)} onClick={() => handleSort(col)} title={`Ordenar por ${col}`}>
                        {col}
                        <span style={{ marginLeft: 5, fontSize: "0.65rem", opacity: sortCol === col ? 1 : 0.3 }}>
                          {sortCol === col ? (sortDir === "asc" ? "↑" : "↓") : "⇅"}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dadosOrdenados.slice(0, 500).map((row, idx) => (
                    <tr key={idx} style={{ background: idx % 2 === 0 ? (darkMode ? "#0f1623" : "#fff") : (darkMode ? "#161b27" : "#f8f9fa") }}>
                      <td style={{ padding: "4px 10px", border: `1px solid ${borda}`, color: "#6c757d", textAlign: "right" }}>{idx + 1}</td>
                      {colunas.map((col) => (
                        <td key={col} style={{ padding: "4px 10px", border: `1px solid ${borda}`, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis" }}>
                          {row[col] == null ? <span style={{ color: "#6c757d", fontStyle: "italic" }}>NULL</span> : String(row[col])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {dadosRaw.length > 500 && (
                <div style={{ padding: "8px 12px", fontSize: "0.75rem", color: "#6c757d", textAlign: "center", background: darkMode ? "#1c2235" : "#e9ecef" }}>
                  Exibindo as primeiras 500 de {dadosRaw.length} linhas
                </div>
              )}
            </div>
          );
        })()}
      </Modal.Body>
      <Modal.Footer style={{ background: darkMode ? "#161b27" : undefined, borderColor: borda, justifyContent: "space-between" }}>
        <Button size="sm" variant="outline-success" onClick={exportarExcel} disabled={!dadosRaw.length}>
          ⬇ Exportar Excel
        </Button>
        <div style={{ display: "flex", gap: 6 }}>
          <Button size="sm" variant="outline-secondary" onClick={() => { onClose(); onRecarregar(); }}>
            <FaSync size={11} className="me-1" /> Recarregar
          </Button>
          <Button size="sm" variant="secondary" onClick={onClose}>Fechar</Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}


// ─── DrillDownModal ───────────────────────────────────────────────────────────
export function DrillDownModal({ show, onClose, titulo, dados, darkMode, onOpenDocumento }) {
  const [busca, setBusca]   = useState("");
  const [pagina, setPagina] = useState(1);
  const ITENS = 20;

  useEffect(() => {
    if (!show) return;
    setBusca("");
    setPagina(1);
  }, [show]);

  const calcularDiasAte = useCallback((valorData) => {
    if (!valorData) return null;
    let d = null;
    const s = String(valorData).trim();
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
      const [dd, mm, yy] = s.split("/").map(Number);
      d = new Date(yy, mm - 1, dd);
    } else if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
      const [yy, mm, dd] = s.slice(0, 10).split("-").map(Number);
      d = new Date(yy, mm - 1, dd);
    } else if (valorData instanceof Date) {
      d = valorData;
    }
    if (!d || Number.isNaN(d.getTime())) return null;
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);
    return Math.round((d.getTime() - hoje.getTime()) / 86400000);
  }, []);

  const dadosFiltrados = useMemo(() => {
    if (!busca.trim()) return dados;
    const b = busca.toLowerCase();
    return dados.filter((row) => {
      const empresa = String(row.empresa || row.fornecedor || row.fantasia || "");
      return [
        String(row.id ?? ""),
        empresa,
        String(row["tipo documento"] ?? ""),
        String(row["nr referencia"] ?? ""),
        String(row["Data Vencimento"] ?? ""),
      ].some((v) => v.toLowerCase().includes(b));
    });
  }, [dados, busca]);

  const totalPaginas = Math.ceil(dadosFiltrados.length / ITENS) || 1;
  const pg           = Math.min(pagina, totalPaginas);
  const paginados    = dadosFiltrados.slice((pg - 1) * ITENS, pg * ITENS);

  const bg     = darkMode ? "#1c2235" : "#fff";
  const borda  = darkMode ? "#30364a" : "#dee2e6";
  const texto  = darkMode ? "#e2e8f0" : "#212529";
  const bgHead = darkMode ? "#151c2e" : "#f8f9fa";

  const getDiasBadge = (dias) => {
    if (dias === null || dias === undefined) return <span className="text-muted small">—</span>;
    if (dias < 0 || dias <= 7) return <Badge bg="danger">{dias} dias</Badge>;
    if (dias <= 30) return <Badge bg="warning" text="dark">{dias} dias</Badge>;
    return <Badge bg="success">{dias} dias</Badge>;
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" scrollable>
      <Modal.Header closeButton style={{ background: bg, borderColor: borda }}>
        <Modal.Title style={{ fontSize: "0.95rem", color: texto }}>{titulo}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ background: bg, padding: "12px 16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 10 }}>
          <input
            className="form-control form-control-sm"
            style={{ maxWidth: 280, background: darkMode ? "#161b27" : undefined, color: darkMode ? "#e2e8f0" : undefined, borderColor: borda }}
            placeholder="Buscar..."
            value={busca}
            onChange={(e) => { setBusca(e.target.value); setPagina(1); }}
          />
          <span style={{ fontSize: "0.8rem", color: "#6c757d", flexShrink: 0 }}>
            {dadosFiltrados.length} documento{dadosFiltrados.length !== 1 ? "s" : ""}
          </span>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table className="table table-sm table-hover mb-0" style={{ fontSize: "0.78rem", color: texto }}>
            <thead>
              <tr style={{ background: bgHead }}>
                <th style={{ width: 56, borderColor: borda, padding: "8px 12px", fontWeight: 700, fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.4px", color: darkMode ? "#94a3b8" : "#64748b", whiteSpace: "nowrap" }}>ID</th>
                <th style={{ borderColor: borda, padding: "8px 12px", fontWeight: 700, fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.4px", color: darkMode ? "#94a3b8" : "#64748b", whiteSpace: "nowrap" }}>Empresa / Fornecedor</th>
                <th style={{ borderColor: borda, padding: "8px 12px", fontWeight: 700, fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.4px", color: darkMode ? "#94a3b8" : "#64748b", whiteSpace: "nowrap" }}>Tipo de Documento</th>
                <th style={{ borderColor: borda, padding: "8px 12px", fontWeight: 700, fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.4px", color: darkMode ? "#94a3b8" : "#64748b", whiteSpace: "nowrap" }}>Nº / Referência</th>
                <th style={{ borderColor: borda, padding: "8px 12px", fontWeight: 700, fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.4px", color: darkMode ? "#94a3b8" : "#64748b", whiteSpace: "nowrap" }}>Vencimento</th>
                <th style={{ borderColor: borda, padding: "8px 12px", fontWeight: 700, fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.4px", color: darkMode ? "#94a3b8" : "#64748b", whiteSpace: "nowrap" }}>Vence em</th>
              </tr>
            </thead>
            <tbody>
              {paginados.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4" style={{ borderColor: borda, background: bg }}>
                    Nenhum documento encontrado
                  </td>
                </tr>
              ) : (
                paginados.map((row, ri) => {
                  const dias = calcularDiasAte(row["Data Vencimento"]);
                  const situacao = String(row.situacao || "").toUpperCase();
                  const isVigente = situacao === "V" || situacao === "";
                  let rowClass = "";
                  let rowBg = undefined;
                  let rowColor = undefined;
                  if (isVigente && dias !== null) {
                    if (dias < 0 || dias <= 7) {
                      if (darkMode) { rowBg = "#3b1219"; rowColor = "#fca5a5"; }
                      else rowClass = "table-danger";
                    } else if (dias <= 30) {
                      if (darkMode) { rowBg = "#2d2008"; rowColor = "#fcd34d"; }
                      else rowClass = "table-warning";
                    }
                  }
                  const empresa = row.empresa || row.fornecedor || row.fantasia || "—";
                  const tipo    = row["tipo documento"] || row.nome_tipo_documento || row.tipo_documento || "—";
                  const nr      = row["nr referencia"] || row.nr_contrato || "—";
                  const vcto    = row["Data Vencimento"] || "—";
                  return (
                    <tr
                      key={ri}
                      className={rowClass}
                      style={{ cursor: onOpenDocumento ? "pointer" : "default", backgroundColor: rowBg, color: rowColor }}
                      onClick={() => onOpenDocumento && row.id != null && onOpenDocumento(row.id)}
                      title={onOpenDocumento ? "Clique para abrir o documento" : undefined}
                    >
                      <td className="fw-bold" style={{ borderColor: borda, padding: "9px 12px", fontSize: "0.875rem", color: darkMode ? "#94a3b8" : undefined }}>{row.id ?? "—"}</td>
                      <td style={{ borderColor: borda, padding: "9px 12px", fontSize: "0.875rem" }}>{empresa}</td>
                      <td style={{ borderColor: borda, padding: "9px 12px", fontSize: "0.875rem" }}>{tipo}</td>
                      <td style={{ borderColor: borda, padding: "9px 12px", fontSize: "0.875rem" }}>{nr}</td>
                      <td style={{ borderColor: borda, padding: "9px 12px", fontSize: "0.875rem" }}>{vcto}</td>
                      <td style={{ borderColor: borda, padding: "9px 12px", fontSize: "0.875rem" }}>{getDiasBadge(dias)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPaginas > 1 && (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, marginTop: 10 }}>
            <button className="btn btn-sm btn-outline-secondary" disabled={pg === 1} onClick={() => setPagina(pg - 1)}>‹</button>
            <span style={{ fontSize: "0.8rem", color: texto }}>{pg} / {totalPaginas}</span>
            <button className="btn btn-sm btn-outline-secondary" disabled={pg === totalPaginas} onClick={() => setPagina(pg + 1)}>›</button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function DashboardCustomizados({ dashboard, onUpdate, onDelete, autoRename = false, onRenameComplete }) {
  const { darkMode, globalData } = useContext(GlobalContext);
  const token      = localStorage.getItem("authToken");
  const entidadeID = globalData?.entidade_id || localStorage.getItem("entidade_id");

  const cfg = dashboard.configuracao ?? {};

  const [dadosRaw,             setDadosRaw]             = useState([]);
  const [dadosCarregando,      setDadosCarregando]      = useState(false);
  const [widgets,              setWidgets]              = useState(cfg.widgets ?? []);
  const [layout,               setLayout]               = useState(cfg.layout  ?? []);
  const [filtrosConfigurados,  setFiltrosConfigurados]  = useState(cfg.filtros_configurados ?? []);
  const [filtrosValoresPadrao, setFiltrosValoresPadrao] = useState(cfg.filtros_valores_padrao ?? {});
  // Prefere valores persistidos do usuário; cai nos padrões configurados se não houver
  const [filtroValores,        setFiltroValores]        = useState(cfg.filtros_valores_usuario ?? cfg.filtros_valores_padrao ?? {});
  const [filtrosData,          setFiltrosData]          = useState(cfg.filtros_data ?? []);
  const [filtrosValoresData,   setFiltrosValoresData]   = useState(cfg.filtros_valores_data ?? {});
  const [editMode,             setEditMode]             = useState(false);
  const [saving,               setSaving]               = useState(false);
  const [showAddWidget,        setShowAddWidget]        = useState(false);
  const [widgetEditando,       setWidgetEditando]       = useState(null);
  const [showFiltrosConfig,    setShowFiltrosConfig]    = useState(false);
  const [renomeando,           setRenomeando]           = useState(autoRename);
  const [nomeEditavel,         setNomeEditavel]         = useState(dashboard.nome);
  const [showDeleteConfirm,    setShowDeleteConfirm]    = useState(false);
  const [showSqlModal,         setShowSqlModal]         = useState(false);
  const [drillDown,            setDrillDown]            = useState({ show: false, dados: [], titulo: "", widget: null });
  const [showDocModal,         setShowDocModal]         = useState(false);
  const [docParaModal,         setDocParaModal]         = useState(null);
  const nomeInputRef      = useRef(null);
  const filtroDebounceRef = useRef(null);

  // Busca todos os dados da procedure uma vez por dashboard
  const fetchDados = useCallback(() => {
    if (!entidadeID) return;
    setDadosCarregando(true);
    api
      .get(`${BASE_URL}/api/relatorioDocumentos/RelatorioDocumentos`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { idEntidade: entidadeID },
      })
      .then((r) => setDadosRaw(Array.isArray(r.data) ? r.data.map(normalizarLinha) : []))
      .catch(() => setDadosRaw([]))
      .finally(() => setDadosCarregando(false));
  }, [token, entidadeID]);

  useEffect(() => { fetchDados(); }, [fetchDados]);

  // Persiste filtros ativos no banco após 600ms de inatividade (checkboxes + datas)
  const persistirFiltros = useCallback((checkboxValores, dataValores) => {
    if (filtroDebounceRef.current) clearTimeout(filtroDebounceRef.current);
    filtroDebounceRef.current = setTimeout(() => {
      api
        .patch(
          `${BASE_URL}/api/dashboardsCustomizados/FiltrosUsuario/${dashboard.id}`,
          { filtros_valores: checkboxValores, filtros_valores_data: dataValores },
          { headers: { Authorization: `Bearer ${token}` } }
        )
        .catch((err) => console.error("Erro ao persistir filtros:", err));
    }, 600);
  }, [dashboard.id, token]);

  const handleFiltroChange = useCallback((novosValores) => {
    setFiltroValores(novosValores);
    persistirFiltros(novosValores, filtrosValoresData);
  }, [persistirFiltros, filtrosValoresData]);

  // Sync ao mudar de dashboard
  useEffect(() => {
    const c = dashboard.configuracao ?? {};
    setWidgets(c.widgets ?? []);
    setLayout(c.layout  ?? []);
    setFiltrosConfigurados(c.filtros_configurados ?? []);
    setFiltrosValoresPadrao(c.filtros_valores_padrao ?? {});
    setFiltroValores(c.filtros_valores_usuario ?? c.filtros_valores_padrao ?? {});
    setFiltrosData(c.filtros_data ?? []);
    setFiltrosValoresData(c.filtros_valores_data ?? {});
    setEditMode(false);
    setNomeEditavel(dashboard.nome);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboard.id]);

  useEffect(() => {
    if (renomeando && nomeInputRef.current) nomeInputRef.current.focus();
  }, [renomeando]);

  // União de todas as chaves das primeiras 20 linhas — garante campos customizados
  // que podem estar ausentes/nulos nos primeiros registros.
  const todasChaves = useMemo(() => {
    if (!dadosRaw.length) return [];
    const seen = new Set();
    const result = [];
    dadosRaw.slice(0, 20).forEach((row) =>
      Object.keys(row).forEach((k) => { if (!seen.has(k)) { seen.add(k); result.push(k); } })
    );
    return result;
  }, [dadosRaw]);

  // Colunas disponíveis derivadas dos dados reais — base para o modal de filtros
  const camposDisponiveis = useMemo(() => todasChaves, [todasChaves]);

  // Campos de data detectados automaticamente — varre até 20 linhas e todas as chaves
  const camposData = useMemo(() => {
    if (!dadosRaw.length) return [];
    const amostra = dadosRaw.slice(0, 20);
    return todasChaves
      .filter((key) => amostra.some((row) => {
        const v = row[key];
        if (v == null) return false;
        const s = String(v);
        return /^\d{2}\/\d{2}\/\d{4}$/.test(s) || /^\d{4}-\d{2}$/.test(s) || /^\d{4}$/.test(s);
      }))
      .map((key) => ({ value: key, label: labelDoCampo(key) }));
  }, [dadosRaw, todasChaves]);

  // Campos customizados não-data: disponíveis como agrupadores/filtros nos demais gráficos.
  // São os campos presentes nos dados mas ausentes tanto em CAMPOS_GRAFICO quanto em camposData.
  const camposCustomizadosNaoData = useMemo(() => {
    const camposDataSet    = new Set(camposData.map((c) => c.value));
    const camposGraficoSet = new Set(CAMPOS_GRAFICO.map((c) => c.value));
    return todasChaves
      .filter((key) => !camposDataSet.has(key) && !camposGraficoSet.has(key) && key !== "nr referencia")
      .map((key) => ({ value: key, label: key }));
  }, [todasChaves, camposData]);

  // Filtragem client-side: checkbox + intervalo de data
  const dadosFiltrados = useMemo(() => {
    if (!dadosRaw.length) return [];
    return dadosRaw.filter((row) => {
      // Filtros checkbox
      const passaCheckbox = filtrosConfigurados.every((filtroKey) => {
        const selecionados = filtroValores[filtroKey];
        if (!selecionados?.length) return true;
        return selecionados.includes(row[filtroKey]);
      });
      if (!passaCheckbox) return false;
      // Filtros de intervalo de data
      return filtrosData.every(({ campo }) => {
        const range = filtrosValoresData[campo];
        if (!range) return true;
        return valorPassaFiltroData(row[campo], range.de, range.ate);
      });
    });
  }, [dadosRaw, filtroValores, filtrosConfigurados, filtrosData, filtrosValoresData]);

  const salvarNoBanco = useCallback(async (novaConfig, novoNome) => {
    setSaving(true);
    try {
      await api.put(
        `${BASE_URL}/api/dashboardsCustomizados/Atualizar/${dashboard.id}`,
        { ...(novoNome !== undefined ? { nome: novoNome } : {}), configuracao: novaConfig },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error("Erro ao salvar dashboard:", err);
    } finally {
      setSaving(false);
    }
  }, [dashboard.id, token, onUpdate]);

  const handleSalvarEdicao = () => {
    const novaConfig = {
      filtros_configurados:    filtrosConfigurados,
      filtros_valores_padrao:  filtrosValoresPadrao,
      filtros_valores_usuario: filtroValores,
      filtros_data:            filtrosData,
      filtros_valores_data:    filtrosValoresData,
      widgets,
      layout,
    };
    salvarNoBanco(novaConfig);
    setEditMode(false);
  };

  const handleCancelarEdicao = () => {
    const c = dashboard.configuracao ?? {};
    setWidgets(c.widgets ?? []);
    setLayout(c.layout  ?? []);
    setFiltrosConfigurados(c.filtros_configurados ?? []);
    setFiltrosValoresPadrao(c.filtros_valores_padrao ?? {});
    setFiltroValores(c.filtros_valores_usuario ?? c.filtros_valores_padrao ?? {});
    setFiltrosData(c.filtros_data ?? []);
    setFiltrosValoresData(c.filtros_valores_data ?? {});
    setEditMode(false);
  };

  const handleLayoutChange = (novoLayout) => setLayout(novoLayout);

  const handleAdicionarWidget = (widgetCfg) => {
    const novoWidget = { ...widgetCfg, i: widgetCfg.i ?? gerarId() };
    const novoLayout = { i: novoWidget.i, x: (widgets.length * 4) % 12, y: Infinity, w: 4, h: 3 };
    const ehEdicao = widgets.some((w) => w.i === novoWidget.i);
    setWidgets((prev) => {
      const idx = prev.findIndex((w) => w.i === novoWidget.i);
      return idx >= 0 ? prev.map((w) => (w.i === novoWidget.i ? novoWidget : w)) : [...prev, novoWidget];
    });
    setLayout((prev) => {
      const idx = prev.findIndex((l) => l.i === novoWidget.i);
      return idx >= 0 ? prev : [...prev, novoLayout];
    });
    // Auto-adicionar filtro de data apenas na inclusão (não na edição)
    if (!ehEdicao && novoWidget.tipo === "bar_timeline" && novoWidget.campo_grupo) {
      const campo  = novoWidget.campo_grupo;
      const rotulo = labelDoCampo(campo);
      setFiltrosData((prev) =>
        prev.find((f) => f.campo === campo) ? prev : [...prev, { campo, rotulo }]
      );
      setFiltrosValoresData((prev) => {
        if (prev[campo]) return prev;
        const anoAtual = new Date().getFullYear();
        return { ...prev, [campo]: { de: `01/01/${anoAtual}`, ate: `31/12/${anoAtual}` } };
      });
    }
    setShowAddWidget(false);
    setWidgetEditando(null);
  };

  const handleEditarWidget  = (w) => { setWidgetEditando(w); setShowAddWidget(true); };
  const handleRemoverWidget = (id) => {
    setWidgets((prev) => prev.filter((w) => w.i !== id));
    setLayout((prev)  => prev.filter((l) => l.i !== id));
  };

  const handleDrillDown = useCallback((widget, label, splitLabel, isTemporal) => {
    let filtered = dadosFiltrados;

    if (label !== null && label !== undefined && widget.campo_grupo) {
      if (isTemporal) {
        // Timeline: label é YYYY-MM, dados têm dd/MM/yyyy → extrair chave e comparar
        filtered = filtered.filter((row) => {
          const v = String(row[widget.campo_grupo] ?? "");
          const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
          const chave = m ? `${m[3]}-${m[2]}` : (/^\d{4}-\d{2}$/.test(v) ? v : null);
          return chave === label;
        });
      } else {
        filtered = filtered.filter((row) => (row[widget.campo_grupo] ?? "—") === label);
      }
    }

    if (splitLabel !== null && splitLabel !== undefined && widget.campo_split) {
      filtered = filtered.filter((row) => (row[widget.campo_split] ?? "—") === splitLabel);
    }

    let titulo = widget.titulo || "Documentos";
    if (label !== null && label !== undefined) {
      titulo += ` — ${label}`;
      if (splitLabel) titulo += ` / ${splitLabel}`;
    }

    setDrillDown({ show: true, dados: filtered, titulo, widget });
  }, [dadosFiltrados]);

  const handleFiltroDataChange = useCallback((campo, tipo, valor) => {
    setFiltrosValoresData((prev) => {
      const novos = { ...prev, [campo]: { ...(prev[campo] ?? {}), [tipo]: valor } };
      persistirFiltros(filtroValores, novos);
      return novos;
    });
  }, [persistirFiltros, filtroValores]);

  const handleSalvarFiltros = (novosFiltros, novosValoresPadrao, novosFiltrosData, novosValoresPadraoData) => {
    setFiltrosConfigurados(novosFiltros);
    setFiltrosValoresPadrao(novosValoresPadrao);
    setFiltroValores({ ...novosValoresPadrao });
    setFiltrosData(novosFiltrosData ?? filtrosData);
    setFiltrosValoresData(novosValoresPadraoData ?? filtrosValoresData);
    persistirFiltros(novosValoresPadrao, novosValoresPadraoData ?? filtrosValoresData);
    setShowFiltrosConfig(false);
  };

  const handleRenomearConfirmar = async () => {
    const nome = nomeEditavel.trim();
    if (!nome) return;
    setSaving(true);
    try {
      await api.put(
        `${BASE_URL}/api/dashboardsCustomizados/Atualizar/${dashboard.id}`,
        { nome },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (onUpdate) onUpdate();
      if (onRenameComplete) onRenameComplete();
    } catch (err) {
      console.error("Erro ao renomear:", err);
    } finally {
      setSaving(false);
      setRenomeando(false);
    }
  };

  const handleExcluir = async () => {
    try {
      await api.delete(
        `${BASE_URL}/api/dashboardsCustomizados/Excluir/${dashboard.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (onDelete) onDelete(dashboard.id);
    } catch (err) {
      console.error("Erro ao excluir dashboard:", err);
    }
    setShowDeleteConfirm(false);
  };

  const bg    = darkMode ? "#0f1623" : "#f0f2f5";
  const borda = darkMode ? "#30364a" : "#dee2e6";
  const texto = darkMode ? "#e2e8f0" : "#212529";

  const layoutComFallback = widgets.map((w, i) => {
    const l = layout.find((l2) => l2.i === w.i);
    return l ?? { i: w.i, x: (i * 4) % 12, y: Math.floor(i / 3) * 3, w: 4, h: 3 };
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {/* Barra de controle */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8, padding: "10px 0", marginBottom: 8,
        borderBottom: `1px solid ${borda}`, flexWrap: "wrap",
      }}>
        {/* Nome */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, minWidth: 0 }}>
          {renomeando ? (
            <>
              <Form.Control
                ref={nomeInputRef}
                value={nomeEditavel}
                onChange={(e) => setNomeEditavel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleRenomearConfirmar();
                  if (e.key === "Escape") {
                    setRenomeando(false);
                    setNomeEditavel(dashboard.nome);
                    if (onRenameComplete) onRenameComplete();
                  }
                }}
                size="sm"
                style={{ maxWidth: 220, fontSize: "0.88rem", background: darkMode ? "#1c2235" : undefined, color: texto, borderColor: borda }}
              />
              <Button size="sm" variant="success" style={{ padding: "2px 8px" }} onClick={handleRenomearConfirmar} disabled={saving}>
                <FaCheck size={11} />
              </Button>
              <Button size="sm" variant="outline-secondary" style={{ padding: "2px 8px" }} onClick={() => { setRenomeando(false); setNomeEditavel(dashboard.nome); if (onRenameComplete) onRenameComplete(); }}>
                <FaTimes size={11} />
              </Button>
            </>
          ) : (
            <span style={{ fontWeight: 600, fontSize: "0.95rem", color: texto, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {dashboard.nome}
            </span>
          )}
        </div>


        {/* Botões */}
        <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap" }}>
          {!editMode ? (
            <>
              <Button size="sm" variant="outline-secondary" onClick={fetchDados} disabled={dadosCarregando} title="Atualizar dados">
                {dadosCarregando ? <Spinner animation="border" size="sm" style={{ width: 11, height: 11 }} /> : <FaSync size={11} />}
              </Button>
              <Button size="sm" variant="outline-dark" onClick={() => setShowSqlModal(true)} title="Ver dados brutos da procedure" style={{ fontFamily: "monospace", fontSize: "0.75rem", letterSpacing: "0.03em" }}>
                SQL
              </Button>
              <Button size="sm" variant="outline-secondary" onClick={() => setEditMode(true)}>
                <FaEdit className="me-1" size={11} /> Editar Layout
              </Button>
              <Button size="sm" variant="outline-secondary" onClick={() => setRenomeando(true)}>
                <FaPencilAlt size={11} className="me-1" /> Renomear
              </Button>
              <Button size="sm" variant="outline-danger" onClick={() => setShowDeleteConfirm(true)}>
                <FaTrash size={11} />
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="outline-info" onClick={() => setShowFiltrosConfig(true)}>
                <FaFilter size={11} className="me-1" /> Filtros
              </Button>
              <Button size="sm" variant="outline-primary" onClick={() => { setWidgetEditando(null); setShowAddWidget(true); }}>
                <FaPlus size={11} className="me-1" /> Widget
              </Button>
              <Button size="sm" variant="success" onClick={handleSalvarEdicao} disabled={saving}>
                {saving ? <Spinner size="sm" animation="border" className="me-1" /> : <FaSave size={11} className="me-1" />}
                Salvar
              </Button>
              <Button size="sm" variant="outline-secondary" onClick={handleCancelarEdicao}>Cancelar</Button>
            </>
          )}
        </div>

      </div>

      {editMode && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: 6 }}>
          <Badge bg="warning" text="dark" style={{ fontSize: "0.75rem", padding: "4px 8px" }}>
            <FaCog size={10} className="me-1" />
            Modo Edição — arraste e redimensione os widgets
          </Badge>
        </div>
      )}

      {/* Loading */}
      {dadosCarregando && (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 120 }}>
          <Spinner animation="border" size="sm" className="me-2" />
          <span style={{ fontSize: "0.85rem", color: "#6c757d" }}>Carregando dados...</span>
        </div>
      )}

      {/* Conteúdo: sidebar + grid */}
      {!dadosCarregando && (
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
          {(filtrosConfigurados.length > 0 || filtrosData.length > 0) && (
            <FiltroSidebar
              filtrosConfigurados={filtrosConfigurados}
              filtroValores={filtroValores}
              onChange={handleFiltroChange}
              dadosRaw={dadosRaw}
              filtrosData={filtrosData}
              filtrosValoresData={filtrosValoresData}
              onFiltroDataChange={handleFiltroDataChange}
              darkMode={darkMode}
            />
          )}

          <div style={{ flex: 1, minWidth: 0, background: bg, borderRadius: 10, padding: 8, border: editMode ? `2px dashed #0d6efd55` : "none" }}>
            {widgets.length === 0 ? (
              <div style={{
                display: "flex", flexDirection: "column", alignItems: "center",
                justifyContent: "center", minHeight: 240, color: "#6c757d", gap: 12,
              }}>
                <span role="img" aria-label="gráfico" style={{ fontSize: "2.5rem", opacity: 0.3 }}>📊</span>
                <div style={{ fontSize: "0.88rem", fontWeight: 500 }}>Nenhum widget configurado</div>
                <Button size="sm" variant="outline-primary" onClick={() => setEditMode(true)}>
                  <FaEdit size={11} className="me-1" /> Entrar no modo de edição
                </Button>
              </div>
            ) : (
              <ResponsiveGridLayout
                className="layout"
                layouts={{ lg: layoutComFallback, md: layoutComFallback, sm: layoutComFallback }}
                cols={LAYOUT_COLS}
                rowHeight={ROW_HEIGHT}
                margin={[10, 10]}
                isDraggable={editMode}
                isResizable={editMode}
                onLayoutChange={handleLayoutChange}
                draggableHandle=".widget-drag-handle"
              >
                {widgets.map((w) => (
                  <div key={w.i} style={{ position: "relative" }}>
                    {editMode && (
                      <div
                        className="widget-drag-handle"
                        style={{
                          position: "absolute", top: 0, left: 0, right: 0, height: 28,
                          background: "rgba(13,110,253,0.12)", borderRadius: "8px 8px 0 0",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          cursor: "move", zIndex: 10, fontSize: "0.7rem", color: "#0d6efd",
                        }}
                      >
                        ⠿ arrastar
                      </div>
                    )}
                    <div style={{ height: "100%", paddingTop: editMode ? 28 : 0 }}>
                      <DashboardWidget
                        widget={w}
                        dados={dadosFiltrados}
                        editMode={editMode}
                        onEdit={() => handleEditarWidget(w)}
                        onDelete={() => handleRemoverWidget(w.i)}
                        onDrillDown={(label, splitLabel, isTemporal) => handleDrillDown(w, label, splitLabel, isTemporal)}
                      />
                    </div>
                  </div>
                ))}
              </ResponsiveGridLayout>
            )}
          </div>
        </div>
      )}

      {/* Modais */}
      <DashboardWidgetConfigModal
        show={showAddWidget}
        onClose={() => { setShowAddWidget(false); setWidgetEditando(null); }}
        widget={widgetEditando}
        onSalvar={handleAdicionarWidget}
        camposData={camposData}
        camposAdicionais={camposCustomizadosNaoData}
      />

      <FiltrosConfigModal
        show={showFiltrosConfig}
        onClose={() => setShowFiltrosConfig(false)}
        filtrosConfigurados={filtrosConfigurados}
        filtrosValoresPadrao={filtrosValoresPadrao}
        dadosRaw={dadosRaw}
        camposDisponiveis={camposDisponiveis}
        camposData={camposData}
        filtrosData={filtrosData}
        filtrosValoresData={filtrosValoresData}
        onSalvar={handleSalvarFiltros}
        darkMode={darkMode}
      />

      <DadosBrutosModal
        show={showSqlModal}
        onClose={() => setShowSqlModal(false)}
        onRecarregar={fetchDados}
        dadosRaw={dadosRaw}
        dadosCarregando={dadosCarregando}
        entidadeID={entidadeID}
        darkMode={darkMode}
      />

      <DrillDownModal
        show={drillDown.show}
        onClose={() => setDrillDown((prev) => ({ ...prev, show: false }))}
        titulo={drillDown.titulo}
        dados={drillDown.dados}
        darkMode={darkMode}
        onOpenDocumento={(id) => { setDocParaModal({ id }); setShowDocModal(true); }}
      />

      {showDocModal && (
        <DocumentosModal
          show={showDocModal}
          handleClose={() => { setShowDocModal(false); setDocParaModal(null); }}
          documento={docParaModal}
          fetchDocumentos={() => {}}
        />
      )}

      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "0.95rem" }}>Excluir Dashboard</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: "0.88rem" }}>
            Confirma a exclusão do dashboard <strong>{dashboard.nome}</strong>?<br />
            Todos os widgets serão removidos.
          </p>
        </Modal.Body>
        <Modal.Footer className="modal-footer-padrao">
          <div className="modal-footer-acoes-esquerda" />
          <div className="modal-footer-acoes-direita">
            <Button variant="light" className="modal-footer-btn-cancelar" onClick={() => setShowDeleteConfirm(false)}>Cancelar</Button>
            <Button variant="danger" className="modal-footer-btn-salvar" onClick={handleExcluir}>Excluir</Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
